import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, Clapperboard, Sparkles, Trash2, Image as ImageIcon, Lightbulb, Download, Wand2, Upload, Share2 } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";
import SocialPublish from "../components/SocialPublish";

const FORMATS = [["reel", "Reel"], ["tiktok", "TikTok"], ["post", "Post"], ["story", "Story"], ["youtube", "YouTube"]];

export default function ContentStudio() {
  const [tab, setTab] = useState("ideas");

  return (
    <div className="space-y-6" data-testid="content-studio-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Clapperboard className="w-7 h-7 text-amber-400" /> Content Studio</h1>
        <p className="text-zinc-500 text-sm mt-1">Idées, scripts et visuels prêts à publier — alignés sur ton objectif.</p>
      </div>

      <div className="flex gap-2 bg-[#18181E] border border-zinc-800 rounded-2xl p-1 w-fit" data-testid="content-tabs">
        <TabBtn active={tab === "ideas"} onClick={() => setTab("ideas")} icon={Lightbulb} label="Idées" testid="tab-ideas" />
        <TabBtn active={tab === "images"} onClick={() => setTab("images")} icon={ImageIcon} label="Images" testid="tab-images" />
        <TabBtn active={tab === "publish"} onClick={() => setTab("publish")} icon={Share2} label="Publier" testid="tab-publish" />
      </div>

      {tab === "ideas" ? <IdeasTab /> : tab === "images" ? <ImagesTab /> : <SocialPublish />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 h-9 rounded-xl text-sm font-medium inline-flex items-center gap-2 transition-colors ${active ? "gold-gradient text-black" : "text-zinc-400 hover:text-zinc-200"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ------------------------------- IDEAS ---------------------------------- */
function IdeasTab() {
  const [pillars, setPillars] = useState([]);
  const [pillar, setPillar] = useState("");
  const [format, setFormat] = useState("reel");
  const [ideas, setIdeas] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => { try { setHistory((await api.get("/content/ideas")).data); } catch {} };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/content/ideas", { pillar: pillar || null, format });
      setIdeas(data.ideas || []);
      if (data.pillars?.length) setPillars(data.pillars);
      toast.success("Idées générées !");
      loadHistory();
    } catch (e) {
      if (e.response?.status !== 402) toast.error("Génération impossible.");
    } finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/content/ideas/${id}`); loadHistory(); };

  return (
    <div className="space-y-6">
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-4" data-testid="content-generator">
        {pillars.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pillars.map((p) => (
              <button key={p} onClick={() => setPillar(p === pillar ? "" : p)} data-testid={`pillar-${p}`}
                className={`px-3 h-8 rounded-full text-xs font-medium border ${pillar === p ? "gold-gradient text-black border-transparent" : "border-zinc-800 text-zinc-300 hover:border-amber-500/50"}`}>{p}</button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {FORMATS.map(([k, l]) => (
            <button key={k} onClick={() => setFormat(k)} data-testid={`format-${k}`}
              className={`px-3 h-8 rounded-full text-xs font-medium border ${format === k ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <button onClick={generate} disabled={loading} data-testid="content-generate-btn"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-5 h-5" /> Générer des idées</>}
        </button>
      </div>

      {ideas.length > 0 && (
        <div className="space-y-3" data-testid="content-ideas">
          {ideas.map((idea, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4" data-testid={`content-idea-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 uppercase">{idea.format}</span>
                <span className="font-semibold text-sm">{idea.title}</span>
              </div>
              {idea.hook && <p className="text-sm text-amber-200/80 mt-2">🎬 « {idea.hook} »</p>}
              {idea.script && <p className="text-sm text-zinc-400 mt-1">{idea.script}</p>}
              <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                {idea.cta && <span>👉 {idea.cta}</span>}
                {idea.why && <span className="text-zinc-600">· {idea.why}</span>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div data-testid="content-history">
          <h2 className="font-display text-lg font-bold mb-3">Historique des idées</h2>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 bg-[#18181E] border border-zinc-800 rounded-xl p-3 hover:border-amber-500/40 transition-colors" data-testid={`content-hist-${h.id}`}>
                <button onClick={() => { setIdeas(h.ideas || []); if (h.pillar) setPillar(h.pillar); if (h.format) setFormat(h.format); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid={`content-hist-view-${h.id}`} className="flex-1 min-w-0 text-left">
                  <span className="text-sm text-zinc-300 truncate block">{(h.ideas?.[0]?.title) || "Idées"} · {h.ideas?.length || 0} idée(s) · {h.format}</span>
                </button>
                <button onClick={() => del(h.id)} className="text-zinc-500 hover:text-red-400 shrink-0" data-testid={`content-hist-del-${h.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- IMAGES --------------------------------- */
function ImagesTab() {
  const [prompt, setPrompt] = useState("");
  const [format, setFormat] = useState("post");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => { try { setImages((await api.get("/content/images")).data); } catch {} };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choisis un fichier image."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("format", format);
      const { data } = await api.post("/content/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image importée — décris la transformation à appliquer.");
      await load();
      setEditing(data); setPrompt(""); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      if (e2.response?.status !== 402) toast.error(e2.response?.data?.detail || "Import impossible.");
    } finally { setUploading(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error(editing ? "Décris la retouche à appliquer." : "Décris l'image à générer."); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.post(`/content/images/${editing.id}/edit`, { prompt, format });
        toast.success("Image retouchée !");
      } else {
        await api.post("/content/images", { prompt, format });
        toast.success("Image générée !");
      }
      setPrompt(""); setEditing(null); load();
    } catch (e) {
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail || "Génération impossible.");
    } finally { setLoading(false); }
  };

  const startEdit = (img) => { setEditing(img); setPrompt(""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { await api.delete(`/content/images/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-4" data-testid="image-generator">
        {editing && (
          <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-2 pl-3" data-testid="image-editing-banner">
            <MediaImg path={editing.media_path} alt="source" small />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-300">Retouche de ce visuel</p>
              <p className="text-[11px] text-zinc-400 truncate">{editing.prompt}</p>
            </div>
            <button onClick={() => { setEditing(null); setPrompt(""); }} data-testid="image-editing-cancel" className="text-zinc-400 hover:text-zinc-200 text-xs px-2">Annuler</button>
          </div>
        )}
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="image-prompt-input"
          placeholder={editing ? "Ex : change le fond en coucher de soleil, ajoute un logo doré, ambiance plus sombre…" : "Ex : visuel d'annonce néon nocturne, portrait lifestyle sous la pluie, ambiance cyberpunk…"}
          className="w-full bg-[#0F0F13] border border-zinc-800 rounded-xl p-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-amber-500/50 outline-none resize-none" />
        <div className="flex flex-wrap gap-2">
          {FORMATS.map(([k, l]) => (
            <button key={k} onClick={() => setFormat(k)} data-testid={`image-format-${k}`}
              className={`px-3 h-8 rounded-full text-xs font-medium border ${format === k ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        <button onClick={generate} disabled={loading} data-testid="image-generate-btn"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {editing ? "Retouche en cours…" : "Génération en cours…"}</> : <>{editing ? <Wand2 className="w-5 h-5" /> : <ImageIcon className="w-5 h-5" />} {editing ? "Retoucher l'image" : "Générer l'image"}</>}
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="image-upload-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || loading} data-testid="image-upload-btn"
            className="h-10 px-4 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm inline-flex items-center gap-2 hover:border-amber-500/50 disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importer une image
          </button>
          <span className="text-[11px] text-zinc-600">à retoucher ou pour t'en inspirer</span>
        </div>
        <p className="text-[11px] text-zinc-600">Généré avec Gemini Nano Banana. La vidéo arrive bientôt.</p>
      </div>

      {images.length === 0 ? (
        <div className="text-center py-12 text-zinc-600 text-sm" data-testid="image-empty">Aucune image pour l'instant. Décris ton visuel ci-dessus 👆</div>
      ) : (
        <div>
          <h2 className="font-display text-lg font-bold mb-3">Mes visuels</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="image-gallery">
            {images.map((img) => (
              <motion.div key={img.id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                className="group relative bg-[#18181E] border border-zinc-800 rounded-2xl overflow-hidden" data-testid={`image-card-${img.id}`}>
                <MediaImg path={img.media_path} alt={img.prompt} />
                <div className="p-2 flex items-center gap-2">
                  <p className="text-[11px] text-zinc-400 line-clamp-2 flex-1">{img.prompt}</p>
                  <button onClick={() => startEdit(img)} data-testid={`image-edit-${img.id}`}
                    className="shrink-0 text-[11px] px-2 h-7 rounded-lg bg-zinc-900 border border-zinc-700 text-amber-300 hover:border-amber-500/50 inline-flex items-center gap-1"><Wand2 className="w-3 h-3" /> Retoucher</button>
                </div>
                <button onClick={() => del(img.id)} data-testid={`image-del-${img.id}`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-black/60 backdrop-blur flex items-center justify-center text-zinc-300 hover:text-red-400 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MediaImg({ path, alt, small }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = false; let obj;
    api.get(`/media/${path}`, { responseType: "blob" }).then((res) => {
      if (!revoked) { obj = URL.createObjectURL(res.data); setSrc(obj); }
    }).catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (small) return src ? <img src={src} alt={alt} className="w-10 h-10 rounded-md object-cover shrink-0" /> : <div className="w-10 h-10 rounded-md bg-zinc-900 shrink-0" />;
  if (!src) return <div className="aspect-square flex items-center justify-center bg-zinc-900"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>;
  return (
    <a href={src} download data-testid="image-download" className="block relative">
      <img src={src} alt={alt} className="w-full aspect-square object-cover" />
      <span className="absolute bottom-2 left-2 w-7 h-7 rounded-lg bg-black/60 backdrop-blur flex items-center justify-center text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"><Download className="w-3.5 h-3.5" /></span>
    </a>
  );
}
